import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatCallDuration } from "../lib/useCall.js";

/**
 * CallOverlay Component
 * Compact call BAR shown at the top of the ChatWindow (below the header) for
 * outgoing / in-progress voice calls, plus the short-lived terminal states
 * ("No answer", "Busy", …). Deliberately NOT a full-window takeover — the
 * chat stays visible and usable during a call, so the visitor can still read
 * and send messages, coupons, or photos while talking.
 * The visitor can be either caller or callee — once connected the UI is
 * identical for both sides.
 */

const ENDED_LABELS = {
    "no-answer": "No answer",
    busy: "Busy",
    unavailable: "Nobody is available right now",
    declined: "Call declined",
    ended: "Call ended",
    cancelled: "Call cancelled",
    "connection-lost": "Connection lost",
    "microphone-blocked": "Microphone blocked — allow access to call",
    "microphone-missing": "No microphone found",
    disabled: "Voice calls are unavailable",
    error: "Call failed",
};

const FAILED_REASONS = new Set([
    "no-answer",
    "busy",
    "unavailable",
    "declined",
    "connection-lost",
    "microphone-blocked",
    "microphone-missing",
    "disabled",
    "error",
]);

export function CallOverlay({ call, darkMode = false }) {
    const { status, peer, muted, speakerOn, durationSec, endedReason } = call;
    const isVisible =
        status === "outgoing" ||
        status === "connecting" ||
        status === "connected" ||
        (status === "ended" && Boolean(endedReason));

    const isRinging = status === "outgoing" || status === "connecting";
    const isConnected = status === "connected";
    const isEnded = status === "ended";
    const isFailed = isEnded && FAILED_REASONS.has(endedReason);

    const statusLabel =
        status === "outgoing"
            ? "Calling…"
            : status === "connecting"
              ? "Connecting…"
              : isConnected
                ? formatCallDuration(durationSec)
                : ENDED_LABELS[endedReason] || "Call ended";

    return (
        <AnimatePresence initial={false}>
            {isVisible && (
                <motion.div
                    key="call-bar"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="tw-shrink-0 tw-overflow-hidden"
                >
                    <div
                        className={`tw-flex tw-items-center tw-gap-3 tw-px-3.5 tw-py-2.5 tw-border-b ${
                            isFailed
                                ? darkMode
                                    ? "tw-bg-red-950/40 tw-border-red-900/40"
                                    : "tw-bg-red-50 tw-border-red-100"
                                : darkMode
                                  ? "tw-bg-gray-800 tw-border-gray-700"
                                  : "tw-bg-primary/5 tw-border-primary/10"
                        }`}
                    >
                        {/* Peer identity */}
                        <div
                            className={`tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-flex-shrink-0 ${
                                darkMode ? "tw-bg-gray-700" : "tw-bg-primary/10"
                            } ${isRinging ? "tw-animate-pulse" : ""}`}
                        >
                            {peer?.avatar ? (
                                <img
                                    src={peer.avatar}
                                    alt={peer?.name || "Caller"}
                                    className="tw-w-full tw-h-full tw-object-cover"
                                />
                            ) : (
                                <svg
                                    className={`tw-w-4 tw-h-4 ${
                                        darkMode
                                            ? "tw-text-gray-300"
                                            : "tw-text-primary"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    />
                                </svg>
                            )}
                        </div>

                        <div className="tw-min-w-0 tw-flex-1">
                            <p
                                className={`tw-m-0 tw-text-sm tw-font-semibold tw-truncate ${
                                    darkMode
                                        ? "tw-text-gray-100"
                                        : "tw-text-gray-800"
                                }`}
                            >
                                {peer?.name || "Voice call"}
                            </p>
                            <p
                                className={`tw-m-0 tw-text-xs tw-truncate ${
                                    isConnected
                                        ? "tw-text-emerald-500 tw-font-medium tw-tabular-nums"
                                        : isFailed
                                          ? darkMode
                                              ? "tw-text-red-300"
                                              : "tw-text-red-500"
                                          : darkMode
                                            ? "tw-text-gray-400"
                                            : "tw-text-gray-500"
                                }`}
                            >
                                {statusLabel}
                            </p>
                        </div>

                        {/* Controls */}
                        {!isEnded && (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                                {isConnected && (
                                    <button
                                        onClick={call.toggleSpeaker}
                                        className={`tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                            speakerOn
                                                ? "tw-bg-primary tw-text-white hover:tw-opacity-90"
                                                : darkMode
                                                  ? "tw-bg-gray-700 tw-text-gray-200 hover:tw-bg-gray-600"
                                                  : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-100 tw-shadow-sm"
                                        }`}
                                        aria-label={speakerOn ? "Speaker off" : "Speaker on"}
                                        title={speakerOn ? "Turn loudspeaker off" : "Turn loudspeaker on"}
                                    >
                                        {speakerOn ? (
                                            <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                                                <path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14" />
                                            </svg>
                                        ) : (
                                            <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                                {isConnected && (
                                    <button
                                        onClick={call.toggleMute}
                                        className={`tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                            muted
                                                ? "tw-bg-amber-500 tw-text-white hover:tw-bg-amber-600"
                                                : darkMode
                                                  ? "tw-bg-gray-700 tw-text-gray-200 hover:tw-bg-gray-600"
                                                  : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-100 tw-shadow-sm"
                                        }`}
                                        aria-label={muted ? "Unmute" : "Mute"}
                                        title={muted ? "Unmute" : "Mute"}
                                    >
                                        {muted ? (
                                            <svg
                                                className="tw-w-4 tw-h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M17 14l4-4m0 4l-4-4"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                className="tw-w-4 tw-h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 11a7 7 0 01-14 0m7 7v3m0-3a7 7 0 007-7M12 4a3 3 0 00-3 3v4a3 3 0 006 0V7a3 3 0 00-3-3z"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                )}

                                <button
                                    onClick={call.hangup}
                                    className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors tw-shadow-sm"
                                    aria-label={
                                        isConnected ? "Hang up" : "Cancel call"
                                    }
                                    title={isConnected ? "Hang up" : "Cancel call"}
                                >
                                    <svg
                                        className="tw-w-4 tw-h-4 tw-rotate-[135deg]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default CallOverlay;
